import {
  Descriptions,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { Alert, Modal } from '@insight-health-ai/roseate/components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { magicFlowApi } from 'src/services';
import { useMagicFlowContext } from '../MagicFlowContext';
import type {
  FlowEvent,
  FlowRunActivity,
  FlowRunPriority,
  FlowRun,
  FlowRunStatus,
  FlowRunStep,
  FlowStep,
} from 'src/models';

type FlowRunDetailsModalProps = {
  open?: boolean;
  onClose?: () => void;
};

const formatTime = (value?: string | null) =>
  value ? dayjs(value).format('MMM D, YYYY h:mm:ss A') : '-';

const formatDuration = (start?: string | null, end?: string | null) => {
  if (!start || !end) return null;
  const ms = dayjs(end).diff(dayjs(start), 'millisecond');
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return `${hours}h ${remMinutes}m`;
};

const statusColor = (label?: string) => {
  if (!label) return 'default';
  const v = label.toLowerCase();
  if (v.includes('complete') || v.includes('success')) return 'green';
  if (v.includes('fail') || v.includes('error') || v.includes('cancel'))
    return 'red';
  if (v.includes('progress') || v.includes('running') || v.includes('active'))
    return 'blue';
  if (v.includes('pending') || v.includes('wait') || v.includes('queue'))
    return 'gold';
  if (v.includes('skip')) return 'default';
  return 'default';
};

const statusLabel = (
  status: FlowRunStatus | string | null | undefined
): string => {
  if (!status) return '-';
  if (typeof status === 'string') return status;
  return status.name || status.key || '-';
};

const priorityLabel = (
  priority: FlowRunPriority | string | null | undefined
): string => {
  if (!priority) return '-';
  if (typeof priority === 'string') return priority;
  return priority.name || priority.key || '-';
};

const stepActionSummary = (step: FlowStep): string => {
  if (step.type === 'action' && step.action?.type) {
    return `action: ${step.action.type}`;
  }
  if (step.type === 'wait' && step.wait?.type) {
    return `wait: ${step.wait.type}`;
  }
  if (step.type === 'condition') {
    const count = step.conditions?.length ?? 0;
    return `condition (${count} branch${count === 1 ? '' : 'es'})`;
  }
  if (step.type === 'subflow' && step.flow) {
    return `subflow: ${step.flow}`;
  }
  if (step.type === 'loop') {
    const max = step.max_iterations;
    return max ? `loop (max ${max})` : 'loop';
  }
  return step.type || '-';
};

const stepTypeColor = (type?: string) => {
  switch (type) {
    case 'start':
      return 'green';
    case 'end':
      return 'default';
    case 'action':
      return 'blue';
    case 'wait':
      return 'gold';
    case 'condition':
      return 'purple';
    case 'loop':
      return 'magenta';
    case 'subflow':
      return 'cyan';
    default:
      return 'default';
  }
};

const renderJsonBlock = (value: unknown) => {
  if (value === null || value === undefined) {
    return <Typography.Text type="secondary">none</Typography.Text>;
  }
  if (typeof value === 'object' && Object.keys(value as object).length === 0) {
    return <Typography.Text type="secondary">empty</Typography.Text>;
  }
  return (
    <pre className="m-0 max-h-64 overflow-auto rounded bg-gray-50 p-2 text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

export const FlowRunDetailsModal = ({
  open: openOverride,
  onClose: onCloseOverride,
}: FlowRunDetailsModalProps = {}) => {
  const {
    scopedHeaders,
    selectedFlowRun,
    closeFlowRunDetails,
    flowRunFlowsById,
  } = useMagicFlowContext();

  const flowRun: FlowRun | null = selectedFlowRun;
  const flow = flowRun?.flow_id ? flowRunFlowsById[flowRun.flow_id] : undefined;
  const isOpen = openOverride ?? Boolean(flowRun);
  const handleClose = onCloseOverride ?? closeFlowRunDetails;

  const flowRunId = flowRun?.flow_run_id;
  const triggerEventId = flowRun?.trigger_event_id || null;

  const { data: detailedRun } = useQuery({
    queryKey: ['magicflow-flow-run-detail', flowRunId],
    enabled: isOpen && Boolean(flowRunId),
    queryFn: () =>
      magicFlowApi
        .get<{ flow_run: FlowRun }>(`/flow_run/${flowRunId}`, {
          headers: scopedHeaders,
        })
        .then((res) => res.data.flow_run),
  });

  const run = detailedRun || flowRun;

  const { data: stepRecords = [], isLoading: isLoadingSteps } = useQuery({
    queryKey: ['magicflow-flow-run-steps', flowRunId],
    enabled: isOpen && Boolean(flowRunId),
    queryFn: () =>
      magicFlowApi
        .get<{ steps: FlowRunStep[] }>(`/flow_run/${flowRunId}/steps`, {
          headers: scopedHeaders,
        })
        .then((res) => res.data.steps || []),
  });

  const { data: activity = [], isLoading: isLoadingActivity } = useQuery({
    queryKey: ['magicflow-flow-run-activity', flowRunId],
    enabled: isOpen && Boolean(flowRunId),
    queryFn: () =>
      magicFlowApi
        .get<{
          activity: FlowRunActivity[];
        }>(`/flow_run/${flowRunId}/activity`, { headers: scopedHeaders })
        .then((res) => res.data.activity || []),
  });

  const { data: triggerEvent } = useQuery({
    queryKey: ['magicflow-event-detail', triggerEventId],
    enabled: isOpen && Boolean(triggerEventId),
    queryFn: () =>
      magicFlowApi
        .get<{ event: FlowEvent }>(`/event/${triggerEventId}`, {
          headers: scopedHeaders,
        })
        .then((res) => res.data.event),
  });

  const stepsSnapshot: FlowStep[] = useMemo(() => {
    const snapshot = run?.steps_snapshot;
    if (Array.isArray(snapshot)) return snapshot as FlowStep[];
    return [];
  }, [run?.steps_snapshot]);

  const currentStepIds = useMemo(
    () => new Set((run?.current_step_ids || []).filter(Boolean)),
    [run?.current_step_ids]
  );

  const stepRunByStepId = useMemo(() => {
    const map: Record<string, FlowRunStep> = {};
    stepRecords.forEach((s) => {
      if (!map[s.step_id]) {
        map[s.step_id] = s;
      } else {
        const prev = map[s.step_id];
        const prevTime = prev.updated_at || prev.created_at || '';
        const currTime = s.updated_at || s.created_at || '';
        if (currTime > prevTime) {
          map[s.step_id] = s;
        }
      }
    });
    return map;
  }, [stepRecords]);

  const stepStateColumns: ColumnsType<FlowRunStep> = [
    {
      title: 'Step',
      dataIndex: 'step_id',
      width: 220,
      render: (value) => (
        <Typography.Text className="font-mono text-xs">{value}</Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (value: string) => (
        <Tag color={statusColor(value)}>{value || '-'}</Tag>
      ),
    },
    {
      title: 'Retries',
      dataIndex: 'retry_count',
      width: 80,
      render: (value) => value ?? 0,
    },
    {
      title: 'Processed by',
      dataIndex: 'processed_by',
      width: 160,
      render: (value) => value || '-',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      width: 180,
      render: (value) => formatTime(value),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      width: 180,
      render: (value) => formatTime(value),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      ellipsis: true,
      render: (value) =>
        value ? (
          <Typography.Text type="danger" className="text-xs">
            {value}
          </Typography.Text>
        ) : (
          '-'
        ),
    },
  ];

  const flowName = flow?.name || run?.flow_id || '-';
  const flowVersion = flow?.version != null ? `v${flow.version}` : null;
  const eventName = triggerEvent?.event_name;

  const duration =
    formatDuration(run?.started_at, run?.completed_at) ||
    formatDuration(run?.created_at, run?.updated_at);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Flow run details"
      width={1100}
    >
      {!run ? (
        <Skeleton active />
      ) : (
        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  <Descriptions
                    size="small"
                    column={2}
                    bordered
                    labelStyle={{ width: 160, fontWeight: 500 }}
                  >
                    <Descriptions.Item label="Run ID">
                      <Typography.Text className="font-mono text-xs">
                        {run.flow_run_id}
                      </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Flow">
                      <Space size={6}>
                        <span>{flowName}</span>
                        {flowVersion ? (
                          <Tag className="!m-0">{flowVersion}</Tag>
                        ) : null}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={statusColor(statusLabel(run.status))}>
                        {statusLabel(run.status)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Priority">
                      {priorityLabel(run.priority)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Started">
                      {formatTime(run.started_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Completed">
                      {formatTime(run.completed_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      {formatTime(run.created_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Updated">
                      {formatTime(run.updated_at)}
                    </Descriptions.Item>
                    {duration ? (
                      <Descriptions.Item label="Duration">
                        {duration}
                      </Descriptions.Item>
                    ) : null}
                    {run.due_date ? (
                      <Descriptions.Item label="Due date">
                        {formatTime(run.due_date)}
                      </Descriptions.Item>
                    ) : null}
                    {run.assigned_to_practitioner_id != null ? (
                      <Descriptions.Item label="Assigned to">
                        practitioner #{run.assigned_to_practitioner_id}
                      </Descriptions.Item>
                    ) : null}
                    {run.parent_flow_run_id ? (
                      <Descriptions.Item label="Parent run">
                        <Typography.Text className="font-mono text-xs">
                          {run.parent_flow_run_id}
                          {run.parent_step_id
                            ? ` (step ${run.parent_step_id})`
                            : ''}
                        </Typography.Text>
                      </Descriptions.Item>
                    ) : null}
                  </Descriptions>
                </div>
              ),
            },
            {
              key: 'trigger',
              label: 'Trigger',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  {triggerEventId ? (
                    <Descriptions
                      size="small"
                      column={2}
                      bordered
                      labelStyle={{ width: 160, fontWeight: 500 }}
                    >
                      <Descriptions.Item label="Event name">
                        {eventName || (
                          <Typography.Text type="secondary">
                            unknown
                          </Typography.Text>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Event ID">
                        <Typography.Text className="font-mono text-xs">
                          {triggerEventId}
                        </Typography.Text>
                      </Descriptions.Item>
                      {triggerEvent?.source ? (
                        <Descriptions.Item label="Source">
                          {triggerEvent.source}
                        </Descriptions.Item>
                      ) : null}
                      {triggerEvent?.created_at ? (
                        <Descriptions.Item label="Received">
                          {formatTime(triggerEvent.created_at)}
                        </Descriptions.Item>
                      ) : null}
                    </Descriptions>
                  ) : (
                    <Typography.Text type="secondary">
                      Manual run (no trigger event).
                    </Typography.Text>
                  )}
                </div>
              ),
            },
            {
              key: 'state',
              label: 'State',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  <Descriptions
                    size="small"
                    column={1}
                    bordered
                    labelStyle={{ width: 160, fontWeight: 500 }}
                  >
                    <Descriptions.Item label="Current steps">
                      {(run.current_step_ids || []).length === 0 ? (
                        <Typography.Text type="secondary">none</Typography.Text>
                      ) : (
                        <Space wrap size={4}>
                          {(run.current_step_ids || []).map((id) => (
                            <Tag key={id} color="blue">
                              {id}
                            </Tag>
                          ))}
                        </Space>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Inputs">
                      {renderJsonBlock(run.inputs)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Custom variables">
                      {renderJsonBlock(run.custom_variables_snapshot)}
                    </Descriptions.Item>
                    {run.loop_state &&
                    Object.keys(run.loop_state).length > 0 ? (
                      <Descriptions.Item label="Loop state">
                        {renderJsonBlock(run.loop_state)}
                      </Descriptions.Item>
                    ) : null}
                    {run.flow_run_metadata &&
                    Object.keys(run.flow_run_metadata).length > 0 ? (
                      <Descriptions.Item label="Metadata">
                        {renderJsonBlock(run.flow_run_metadata)}
                      </Descriptions.Item>
                    ) : null}
                  </Descriptions>
                </div>
              ),
            },
            {
              key: 'flow',
              label: 'Flow',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  {stepsSnapshot.length === 0 ? (
                    <Alert
                      appearance="info"
                      message="No steps snapshot available for this run."
                    />
                  ) : (
                    <div className="rounded border border-gray-200">
                      {stepsSnapshot.map((step, idx) => {
                        const stepId = step.id || `#${idx}`;
                        const isCurrent = currentStepIds.has(stepId);
                        return (
                          <div
                            key={stepId + idx}
                            className={`border-b border-gray-100 px-3 py-2 last:border-b-0 ${
                              isCurrent ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Tag
                                color={stepTypeColor(step.type)}
                                className="!m-0 font-mono"
                              >
                                {step.type || '?'}
                              </Tag>
                              <Typography.Text className="font-mono text-xs">
                                {stepId}
                              </Typography.Text>
                              {step.name ? (
                                <Typography.Text className="text-sm">
                                  {step.name}
                                </Typography.Text>
                              ) : null}
                              {isCurrent ? (
                                <Tag color="blue" className="!m-0">
                                  current
                                </Tag>
                              ) : null}
                            </div>
                            <div className="mt-1 text-xs text-gray-600">
                              {stepActionSummary(step)}
                              {step.next ? (
                                <span className="ml-2">
                                  →{' '}
                                  <span className="font-mono">{step.next}</span>
                                </span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'step-state',
              label: 'Step state',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  <Table
                    size="small"
                    rowKey="flow_run_step_id"
                    dataSource={stepRecords}
                    columns={stepStateColumns}
                    loading={isLoadingSteps}
                    pagination={false}
                    scroll={{ x: 1000 }}
                    locale={{
                      emptyText: 'No step state recorded yet',
                    }}
                  />
                  {stepsSnapshot.length > 0 && stepRecords.length > 0 ? (
                    <div className="mt-2 text-xs text-gray-500">
                      {Object.keys(stepRunByStepId).length} of{' '}
                      {stepsSnapshot.length} steps have run records.
                    </div>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'activity',
              label: 'Activity',
              children: (
                <div className="max-h-[65vh] overflow-auto pr-1">
                  {isLoadingActivity ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                  ) : activity.length === 0 ? (
                    <Typography.Text type="secondary">
                      No activity recorded.
                    </Typography.Text>
                  ) : (
                    <Timeline
                      items={activity.map((entry) => ({
                        color: statusColor(entry.event) || 'gray',
                        children: (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Typography.Text strong className="capitalize">
                                {entry.event.replace(/_/g, ' ')}
                              </Typography.Text>
                              {entry.step_id ? (
                                <Tag className="!m-0 font-mono text-xs">
                                  {entry.step_id}
                                </Tag>
                              ) : null}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatTime(entry.created_at)}
                              {entry.user ? ` • ${entry.user}` : ''}
                            </div>
                            {entry.metadata &&
                            Object.keys(entry.metadata).length > 0 ? (
                              <div>{renderJsonBlock(entry.metadata)}</div>
                            ) : null}
                          </div>
                        ),
                      }))}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
};
